import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ShieldCheck, Plus, Trash, Calculator, FileText, Warning, CheckCircle, Globe, SealCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { COUNTRIES, countryName } from "@/lib/countries";

const SYMBOLS = { usd: "$", eur: "€", gbp: "£", inr: "₹", aed: "AED ", cad: "C$", aud: "A$", jpy: "¥" };
const money = (n, cur) =>
  `${SYMBOLS[(cur || "usd").toLowerCase()] || `${(cur || "usd").toUpperCase()} `}${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const COVERS = [
  ["cargo", "Cargo / goods in transit"],
  ["fleet", "Fleet & vehicle"],
  ["cart", "Local cart carrier"],
  ["business", "Business & equipment"],
  ["liability", "Public & authority liability"],
];
const coverLabel = (k) => (COVERS.find(([c]) => c === k) || [null, k])[1];
const BASES = [["per_shipment", "Per shipment"], ["per_trip", "Per trip"], ["per_vehicle", "Per vehicle"], ["annual", "Annual"]];
const CURRENCY_OPTIONS = ["usd", "eur", "gbp", "inr", "aed", "cad", "aud", "jpy", "zar", "kes", "ngn", "sgd"];

const BLANK_SEARCH = { cover_type: "cargo", insured_value: 25000, country: "US", dest_country: "US" };
const BLANK_PROVIDER = {
  name: "", cover_types: ["cargo"], countries: [], international: false, currency: "usd",
  contact_person: "", email: "", phone: "", website: "", regulator: "", licence_no: "",
  rating: "", description: "", active: true,
};
const BLANK_PLAN = {
  provider_id: "", name: "", cover_type: "cargo", basis: "per_shipment", scope: "both",
  countries: [], currency: "usd", rate_pct: 0.5, min_premium: 50, max_cover: "", excess: 0,
  term_days: 30, notes: "", active: true,
};
const BLANK_ENQUIRY = {
  cover_type: "cargo", insured_value: 25000, currency: "usd", country: "US", dest_country: "",
  provider_id: "", details: "",
};
const BLANK_CLAIM = { policy_id: "", incident_date: new Date().toISOString().slice(0, 10), description: "", amount: 0 };

export default function Insurance() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState("cover");
  const [summary, setSummary] = useState(null);
  const [providers, setProviders] = useState([]);
  const [plans, setPlans] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [claims, setClaims] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [offers, setOffers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [fForm, setFForm] = useState({
    name: "", facility_type: "warehouse", address: "", city: "", country: "US",
    value: 0, currency: "usd", notes: "",
  });
  const [coverFacility, setCoverFacility] = useState(null);
  const [coverPlanId, setCoverPlanId] = useState("");
  const [search, setSearch] = useState({ ...BLANK_SEARCH });
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [shipmentId, setShipmentId] = useState("");
  const [dialog, setDialog] = useState(null);
  const [pForm, setPForm] = useState({ ...BLANK_PROVIDER });
  const [planForm, setPlanForm] = useState({ ...BLANK_PLAN });
  const [eForm, setEForm] = useState({ ...BLANK_ENQUIRY });
  const [cForm, setCForm] = useState({ ...BLANK_CLAIM });
  const [respondTo, setRespondTo] = useState(null);
  const [rForm, setRForm] = useState({ premium: 0, currency: "usd", excess: 0, term_days: 30, note: "" });

  const isAdmin = user?.role === "admin";
  const myProviders = providers.filter((p) => p.owner_id === user?.id);

  const load = useCallback(async () => {
    const [s, pr, pl, po, cl, en, of, fa] = await Promise.all([
      api.get("/insurance/summary"),
      api.get("/insurance/providers"),
      api.get("/insurance/plans"),
      api.get("/insurance/policies"),
      api.get("/insurance/claims"),
      api.get("/insurance/quote-requests"),
      api.get("/insurance/offers"),
      api.get("/insurance/facilities"),
    ]);
    setSummary(s.data); setProviders(pr.data); setPlans(pl.data);
    setPolicies(po.data); setClaims(cl.data); setEnquiries(en.data);
    setOffers(of.data); setFacilities(fa.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const find = useCallback(async (payload) => {
    setSearching(true);
    try {
      const { data } = await api.post("/insurance/quotes", {
        cover_type: payload.cover_type,
        insured_value: Number(payload.insured_value),
        country: payload.country || null,
        dest_country: payload.dest_country || null,
      });
      setOptions(data.options);
    } catch (err) {
      setOptions([]);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSearching(false);
    }
  }, []);

  // Deep link from the shipping booking flow: /insurance?shipment=..&value=..&country=..&dest=..
  useEffect(() => {
    const sid = params.get("shipment");
    if (!sid) return;
    const prefill = {
      cover_type: "cargo",
      insured_value: Number(params.get("value") || 0) || 1000,
      country: params.get("country") || "US",
      dest_country: params.get("dest") || params.get("country") || "US",
    };
    setShipmentId(sid);
    setSearch(prefill);
    setView("cover");
    find(prefill);
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (option) => {
    try {
      const { data } = await api.post("/insurance/policies", {
        plan_id: option.plan_id,
        insured_value: Number(search.insured_value),
        shipment_id: shipmentId || null,
      });
      toast.success(`On cover — policy ${data.policy_number}`);
      setShipmentId("");
      setView("policies");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const payPremium = async (policy) => {
    try {
      const { data } = await api.post("/payments/insurance/checkout", {
        policy_id: policy.id,
        origin_url: window.location.origin,
        user_id: user?.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const certificate = async (policy) => {
    try {
      const res = await api.get(`/insurance/policies/${policy.id}/certificate`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${policy.policy_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not build that certificate");
    }
  };

  const saveProvider = async (e) => {
    e.preventDefault();
    try {
      await api.post("/insurance/providers", {
        ...pForm,
        rating: pForm.rating === "" ? null : Number(pForm.rating),
      });
      toast.success(isAdmin ? "Provider added to the directory" : "Registered — an admin will verify you");
      setDialog(null);
      setPForm({ ...BLANK_PROVIDER });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const savePlan = async (e) => {
    e.preventDefault();
    try {
      await api.post("/insurance/plans", {
        ...planForm,
        rate_pct: Number(planForm.rate_pct),
        min_premium: Number(planForm.min_premium),
        max_cover: planForm.max_cover === "" ? null : Number(planForm.max_cover),
        excess: Number(planForm.excess),
        term_days: Number(planForm.term_days),
      });
      toast.success("Cover plan published");
      setDialog(null);
      setPlanForm({ ...BLANK_PLAN });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendEnquiry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/insurance/quote-requests", {
        ...eForm,
        insured_value: Number(eForm.insured_value),
        provider_id: eForm.provider_id || null,
        dest_country: eForm.dest_country || null,
      });
      toast.success("Enquiry sent to matching insurers");
      setDialog(null);
      setEForm({ ...BLANK_ENQUIRY });
      setView("enquiries");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendResponse = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/insurance/quote-requests/${respondTo.id}/respond`, {
        premium: Number(rForm.premium),
        currency: rForm.currency,
        excess: Number(rForm.excess),
        term_days: Number(rForm.term_days),
        note: rForm.note || null,
      });
      toast.success("Quote sent");
      setRespondTo(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const acceptQuote = async (enquiry, index) => {
    try {
      const { data } = await api.post(`/insurance/quote-requests/${enquiry.id}/accept`, { response_index: index });
      toast.success(`Policy ${data.policy_number} issued`);
      setView("policies");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const fileClaim = async (e) => {
    e.preventDefault();
    try {
      await api.post("/insurance/claims", { ...cForm, amount: Number(cForm.amount) });
      toast.success("Claim filed");
      setDialog(null);
      setCForm({ ...BLANK_CLAIM });
      setView("claims");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setClaimStatus = async (claim, status) => {
    try {
      await api.patch(`/insurance/claims/${claim.id}/status`, { status });
      toast.success(`Claim ${status.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveFacility = async (e) => {
    e.preventDefault();
    try {
      await api.post("/insurance/facilities", { ...fForm, value: Number(fForm.value) });
      toast.success("Facility added to the register");
      setDialog(null);
      setFForm({ name: "", facility_type: "warehouse", address: "", city: "", country: "US", value: 0, currency: "usd", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const insureFacility = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/insurance/facilities/cover", {
        facility_id: coverFacility.id,
        plan_id: coverPlanId,
        insured_value: Number(coverFacility.value) || null,
      });
      toast.success(`${coverFacility.name} is on cover — policy ${data.policy_number}`);
      setCoverFacility(null);
      setCoverPlanId("");
      setView("policies");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const renew = async (policy) => {
    try {
      const { data } = await api.post(`/insurance/policies/${policy.id}/renew`, {});
      toast.success(`Renewed as ${data.policy_number} — pay the premium to confirm`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const act = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleCover = (key) =>
    setPForm((f) => ({
      ...f,
      cover_types: f.cover_types.includes(key)
        ? f.cover_types.filter((c) => c !== key)
        : [...f.cover_types, key],
    }));

  const VIEWS = [
    ["cover", "Get cover"],
    ["providers", `Providers (${providers.length})`],
    ["policies", `Policies (${policies.length})`],
    ["offers", `Offers (${offers.filter((o) => o.status === "offered").length})`],
    ["facilities", `Facilities (${facilities.length})`],
    ["enquiries", `Enquiries (${enquiries.length})`],
    ["claims", `Claims (${claims.length})`],
  ];

  return (
    <div data-testid="insurance-page">
      <PageHeader
        overline="Risk"
        title="Insurance"
        description="Insurers by country and worldwide — cargo in transit, fleets and local cart carriers, warehouses and authority liability. Compare premiums instantly, ask for a bespoke quote, hold your policies and certificates, and file a claim."
        testid="insurance-header"
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="request-quote-button"
              variant="outline"
              onClick={() => { setEForm({ ...BLANK_ENQUIRY }); setDialog("enquiry"); }}
              className="w-full sm:w-auto"
            >
              <Calculator size={16} className="mr-1.5" /> Request a quote
            </Button>
            {user?.role !== "member" && (
              <Button
                data-testid="add-provider-button"
                onClick={() => { setPForm({ ...BLANK_PROVIDER }); setDialog("provider"); }}
                className="w-full sm:w-auto"
              >
                <Plus size={16} className="mr-1.5" /> {isAdmin ? "Add insurer" : "Register as insurer"}
              </Button>
            )}
          </div>
        }
      />

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard testid="stat-providers" label="Insurers listed" value={summary.providers} icon={ShieldCheck} />
          <StatCard testid="stat-policies" label="Active policies" value={summary.active_policies} tone="primary" icon={FileText} />
          <StatCard testid="stat-insured" label="Sum insured" value={money(summary.insured_value)} icon={Globe} />
          <StatCard testid="stat-premium-unpaid" label="Premium unpaid" value={money(summary.premium_unpaid)} tone={summary.premium_unpaid > 0 ? "danger" : "default"} />
          <StatCard testid="stat-open-claims" label="Open claims" value={summary.open_claims} tone={summary.open_claims > 0 ? "danger" : "success"} icon={Warning} />
        </div>
      )}

      <Tabs value={view} onValueChange={setView} className="mb-5">
        <TabsList className="w-full justify-start overflow-x-auto" data-testid="insurance-views">
          {VIEWS.map(([value, label]) => (
            <TabsTrigger key={value} value={value} data-testid={`view-${value}`} className="whitespace-nowrap">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* ---------------- Get cover ---------------- */}
      {view === "cover" && (
        <div className="space-y-5" data-testid="cover-panel">
          {shipmentId && (
            <div className="border border-[#C7D2FE] bg-[#EEF2FF] p-3 text-sm text-[#002FA7]" data-testid="insuring-shipment-banner">
              Insuring the load you just booked — pick a plan below and the certificate is attached to that shipment.
            </div>
          )}
          <div className="grid gap-3 border border-border bg-white p-4 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <Label>Cover</Label>
              <Select value={search.cover_type} onValueChange={(v) => setSearch((s) => ({ ...s, cover_type: v }))}>
                <SelectTrigger data-testid="search-cover-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COVERS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sum insured</Label>
              <Input
                data-testid="search-insured-value"
                type="number"
                min="1"
                value={search.insured_value}
                onChange={(e) => setSearch((s) => ({ ...s, insured_value: e.target.value }))}
              />
            </div>
            <div>
              <Label>Country</Label>
              <Select value={search.country} onValueChange={(v) => setSearch((s) => ({ ...s, country: v }))}>
                <SelectTrigger data-testid="search-country"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destination (for transit)</Label>
              <Select value={search.dest_country} onValueChange={(v) => setSearch((s) => ({ ...s, dest_country: v }))}>
                <SelectTrigger data-testid="search-dest-country"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button data-testid="find-cover-button" onClick={() => find(search)} disabled={searching} className="w-full">
                <Calculator size={16} className="mr-1.5" /> {searching ? "Pricing…" : "Compare cover"}
              </Button>
            </div>
          </div>

          {options.length === 0 ? (
            <EmptyState
              testid="no-cover-options"
              title="No premiums yet"
              description="Set the cover type, sum insured and country, then compare cover. If nothing matches, request a bespoke quote."
            />
          ) : (
            <div className="space-y-3" data-testid="cover-options">
              {options.map((o) => (
                <div
                  key={o.plan_id}
                  data-testid={`cover-option-${o.plan_id}`}
                  className="flex flex-col gap-3 border border-border bg-white p-4 transition-colors duration-150 hover:border-slate-400 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-[#0F172A]">{o.plan_name}</span>
                      {o.cheapest && (
                        <span data-testid={`cheapest-${o.plan_id}`} className="border border-[#A7E3CE] bg-[#E6F7F1] px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-[#0B7A5B]">
                          Lowest premium
                        </span>
                      )}
                      {o.verified && (
                        <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wider text-[#002FA7]">
                          <SealCheck size={13} /> Verified
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {o.provider_name} · {coverLabel(o.cover_type)} · {o.scope} · {o.basis.replace(/_/g, " ")} · {o.term_days} day term
                    </div>
                    <div className="text-xs text-slate-500">
                      {o.rate_pct}% of value · excess {money(o.excess, o.currency)}
                      {o.regulator ? ` · ${o.regulator}` : ""}
                      {o.rating ? ` · ★ ${o.rating}` : ""}
                    </div>
                    {o.notes && <div className="mt-1 text-xs text-slate-400">{o.notes}</div>}
                  </div>
                  <div className="flex items-center gap-3 sm:shrink-0">
                    <div className="text-right">
                      <div className="font-mono text-base font-bold text-[#002FA7]" data-testid={`premium-${o.plan_id}`}>
                        {money(o.premium, o.currency)}
                      </div>
                      <div className="text-[0.65rem] text-slate-400">premium</div>
                    </div>
                    <Button size="sm" data-testid={`buy-cover-${o.plan_id}`} onClick={() => buy(o)}>
                      Take cover
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- Providers ---------------- */}
      {view === "providers" && (
        <div className="space-y-3" data-testid="providers-panel">
          {providers.length === 0 ? (
            <EmptyState testid="no-providers" title="No insurers yet" description="Add the first insurer to the directory." />
          ) : providers.map((p) => (
            <div key={p.id} data-testid={`provider-${p.id}`} className="border border-border bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-[#0F172A]">{p.name}</span>
                    {p.verified
                      ? <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wider text-[#002FA7]" data-testid={`verified-${p.id}`}><SealCheck size={13} /> Verified</span>
                      : <StatusBadge value="pending" testid={`unverified-${p.id}`} />}
                    {p.international && (
                      <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-wider text-slate-500">
                        <Globe size={13} /> International
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {(p.cover_types || []).map(coverLabel).join(" · ")}
                  </div>
                  <div className="text-xs text-slate-500" data-testid={`provider-countries-${p.id}`}>
                    {p.countries?.length ? p.countries.map(countryName).join(", ") : "Every country"}
                    {p.regulator ? ` · ${p.regulator}` : ""}
                    {p.rating ? ` · ★ ${p.rating}` : ""}
                  </div>
                  {p.description && <div className="mt-1.5 text-xs text-slate-500">{p.description}</div>}
                  <div className="mt-1.5 text-xs text-slate-600">
                    {[p.contact_person, p.email, p.phone].filter(Boolean).join(" · ") || "No contact on file"}
                  </div>
                  <div className="mt-2 text-xs text-slate-400">
                    {plans.filter((pl) => pl.provider_id === p.id).length} published plan(s)
                    {p.owner_name ? ` · managed by ${p.owner_name}` : " · directory listing"}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`enquire-provider-${p.id}`}
                    onClick={() => { setEForm({ ...BLANK_ENQUIRY, provider_id: p.owner_id ? p.id : "" }); setDialog("enquiry"); }}
                  >
                    Ask for a quote
                  </Button>
                  {!p.owner_id && user?.role !== "member" && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`claim-provider-${p.id}`}
                      onClick={() => act(() => api.post(`/insurance/providers/${p.id}/claim`), "Listing claimed — awaiting verification")}
                    >
                      This is us
                    </Button>
                  )}
                  {(p.owner_id === user?.id || isAdmin) && (
                    <Button
                      size="sm"
                      data-testid={`add-plan-${p.id}`}
                      onClick={() => { setPlanForm({ ...BLANK_PLAN, provider_id: p.id }); setDialog("plan"); }}
                    >
                      <Plus size={14} className="mr-1" /> Plan
                    </Button>
                  )}
                  {isAdmin && !p.verified && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`verify-provider-${p.id}`}
                      onClick={() => act(() => api.post(`/insurance/providers/${p.id}/verify`), "Provider verified")}
                    >
                      <CheckCircle size={14} className="mr-1" /> Verify
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`delete-provider-${p.id}`}
                      onClick={() => act(() => api.delete(`/insurance/providers/${p.id}`), "Provider removed")}
                    >
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              </div>
              {plans.filter((pl) => pl.provider_id === p.id).length > 0 && (
                <div className="mt-3 grid gap-2 border-t border-border pt-3 sm:grid-cols-2">
                  {plans.filter((pl) => pl.provider_id === p.id).map((pl) => (
                    <div key={pl.id} data-testid={`plan-${pl.id}`} className="border border-border bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate text-xs font-semibold text-[#0F172A]">{pl.name}</div>
                          <div className="text-[0.7rem] text-slate-500">
                            {coverLabel(pl.cover_type)} · {pl.rate_pct}% · min {money(pl.min_premium, pl.currency)} · {pl.scope}
                          </div>
                          <div className="text-[0.7rem] text-slate-400">
                            excess {money(pl.excess, pl.currency)} · {pl.term_days}d
                            {pl.max_cover ? ` · up to ${money(pl.max_cover, pl.currency)}` : ""}
                          </div>
                        </div>
                        {(p.owner_id === user?.id || isAdmin) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`delete-plan-${pl.id}`}
                            onClick={() => act(() => api.delete(`/insurance/plans/${pl.id}`), "Plan removed")}
                          >
                            <Trash size={13} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Policies ---------------- */}
      {view === "policies" && (
        <div className="space-y-3" data-testid="policies-panel">
          {policies.length === 0 ? (
            <EmptyState testid="no-policies" title="No policies yet" description="Compare cover and take out your first policy." />
          ) : policies.map((p) => (
            <div key={p.id} data-testid={`policy-${p.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-[#002FA7]">{p.policy_number}</span>
                  <StatusBadge value={p.status} testid={`policy-status-${p.id}`} />
                  <StatusBadge value={p.payment_status === "paid" ? "approved" : "pending"} testid={`policy-payment-${p.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {coverLabel(p.cover_type)} · {p.plan_name} · {p.provider_name || "—"}
                </div>
                <div className="text-xs text-slate-500">
                  Sum insured {money(p.insured_value, p.currency)} · premium {money(p.premium, p.currency)} ·
                  excess {money(p.excess, p.currency)} · {p.start_date} → {p.end_date}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 sm:shrink-0">
                {p.payment_status !== "paid" && p.status === "active" && (
                  <Button size="sm" data-testid={`pay-premium-${p.id}`} onClick={() => payPremium(p)}>
                    Pay premium
                  </Button>
                )}
                <Button size="sm" variant="outline" data-testid={`certificate-${p.id}`} onClick={() => certificate(p)}>
                  <FileText size={14} className="mr-1" /> Certificate
                </Button>
                {p.status === "active" && !p.renewed_into && (
                  <Button size="sm" variant="outline" data-testid={`renew-policy-${p.id}`} onClick={() => renew(p)}>
                    Renew
                  </Button>
                )}
                {p.status === "active" && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`file-claim-${p.id}`}
                      onClick={() => { setCForm({ ...BLANK_CLAIM, policy_id: p.id }); setDialog("claim"); }}
                    >
                      File a claim
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`cancel-policy-${p.id}`}
                      onClick={() => act(() => api.post(`/insurance/policies/${p.id}/cancel`), "Policy cancelled")}
                    >
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Enquiries ---------------- */}
      {view === "enquiries" && (
        <div className="space-y-3" data-testid="enquiries-panel">
          {enquiries.length === 0 ? (
            <EmptyState testid="no-enquiries" title="No cover enquiries" description="Ask matching insurers for a bespoke premium." />
          ) : enquiries.map((q) => (
            <div key={q.id} data-testid={`enquiry-${q.id}`} className="border border-border bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-[#0F172A]">{coverLabel(q.cover_type)}</span>
                    <StatusBadge value={q.status} testid={`enquiry-status-${q.id}`} />
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {money(q.insured_value, q.currency)} sum insured · {countryName(q.country) || "any country"}
                    {q.dest_country ? ` → ${countryName(q.dest_country)}` : ""} · from {q.requester_name}
                  </div>
                  {q.details && <div className="mt-1 text-xs text-slate-500">{q.details}</div>}
                </div>
                {(myProviders.length > 0 || isAdmin) && q.status !== "accepted" && (
                  <Button
                    size="sm"
                    data-testid={`respond-enquiry-${q.id}`}
                    onClick={() => { setRForm({ premium: 0, currency: q.currency || "usd", excess: 0, term_days: 30, note: "" }); setRespondTo(q); }}
                  >
                    Quote it
                  </Button>
                )}
              </div>
              {(q.responses || []).length > 0 && (
                <div className="mt-3 space-y-2 border-t border-border pt-3">
                  {q.responses.map((r, i) => (
                    <div key={i} data-testid={`response-${q.id}-${i}`} className="flex flex-col gap-2 bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-xs text-slate-600">
                        <span className="font-semibold text-[#0F172A]">{r.provider_name}</span> ·
                        {" "}{money(r.premium, r.currency)} premium · excess {money(r.excess, r.currency)} · {r.term_days}d
                        {r.note ? ` · ${r.note}` : ""}
                      </div>
                      {q.requested_by === user?.id && q.status !== "accepted" && (
                        <Button size="sm" data-testid={`accept-response-${q.id}-${i}`} onClick={() => acceptQuote(q, i)}>
                          Accept
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Claims ---------------- */}
      {view === "claims" && (
        <div className="space-y-3" data-testid="claims-panel">
          {claims.length === 0 ? (
            <EmptyState testid="no-claims" title="No claims" description="Nothing has gone wrong yet — long may it last." />
          ) : claims.map((c) => (
            <div key={c.id} data-testid={`claim-${c.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-[#002FA7]">{c.claim_number}</span>
                  <StatusBadge value={c.status} testid={`claim-status-${c.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {c.policy_number} · {coverLabel(c.cover_type)} · incident {c.incident_date} ·
                  {" "}{money(c.amount, c.currency)} claimed by {c.claimant_name}
                </div>
                <div className="mt-1 text-xs text-slate-500">{c.description}</div>
                {c.assessor_note && <div className="mt-1 text-xs text-slate-400">Assessor: {c.assessor_note}</div>}
              </div>
              <div className="flex flex-wrap gap-2 sm:shrink-0">
                {["under_review", "approved", "rejected", "paid"].map((s) => (
                  <Button
                    key={s}
                    size="sm"
                    variant={s === "paid" ? "default" : "outline"}
                    data-testid={`claim-${s}-${c.id}`}
                    onClick={() => setClaimStatus(c, s)}
                  >
                    {s.replace("_", " ")}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Offers from agents ---------------- */}
      {view === "offers" && (
        <div className="space-y-3" data-testid="offers-panel">
          {offers.length === 0 ? (
            <EmptyState testid="no-offers" title="No offers of cover" description="Local agents can offer cover on your shipments and facilities — their offers land here." />
          ) : offers.map((o) => (
            <div key={o.id} data-testid={`offer-${o.id}`} className="flex flex-col gap-3 border border-border bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">{o.subject_label}</span>
                  <StatusBadge value={o.status} testid={`offer-status-${o.id}`} />
                </div>
                <div className="mt-1 text-xs text-slate-600">
                  {o.provider_name} · {coverLabel(o.cover_type)} · {money(o.insured_value, o.currency)} insured
                </div>
                <div className="text-xs text-slate-500">
                  {money(o.premium, o.currency)} premium · excess {money(o.excess, o.currency)} · {o.term_days} day term
                </div>
                {o.note && <div className="mt-1 text-xs text-slate-400">{o.note}</div>}
              </div>
              {o.status === "offered" && o.target_user_id === user?.id && (
                <div className="flex flex-wrap gap-2 sm:shrink-0">
                  <Button size="sm" data-testid={`accept-offer-${o.id}`} onClick={() => act(() => api.post(`/insurance/offers/${o.id}/accept`), "On cover")}>
                    Accept
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`decline-offer-${o.id}`} onClick={() => act(() => api.post(`/insurance/offers/${o.id}/decline`), "Offer declined")}>
                    Decline
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------------- Facility register ---------------- */}
      {view === "facilities" && (
        <div className="space-y-3" data-testid="facilities-panel">
          <div className="flex justify-end">
            <Button data-testid="add-facility-button" onClick={() => setDialog("facility")}>
              <Plus size={16} className="mr-1.5" /> Add facility
            </Button>
          </div>
          {facilities.length === 0 ? (
            <EmptyState testid="no-facilities" title="No facilities registered" description="Add your warehouses, depots, yards and workshops with their value so agents can quote cover on them." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {facilities.map((f) => (
                <div key={f.id} data-testid={`facility-${f.id}`} className="flex items-start justify-between gap-3 border border-border bg-white p-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0F172A]">{f.name}</div>
                    <div className="text-xs text-slate-500">
                      {f.facility_type} · {[f.city, countryName(f.country)].filter(Boolean).join(", ")}
                    </div>
                    <div className="text-xs text-slate-500">
                      {f.value ? `Valued ${money(f.value, f.currency)}` : "No value on file"}
                    </div>
                    {f.address && <div className="mt-1 text-xs text-slate-400">{f.address}</div>}
                  </div>
                  <div className="flex shrink-0 flex-col gap-2">
                    <Button
                      size="sm"
                      data-testid={`insure-facility-${f.id}`}
                      onClick={() => { setCoverFacility(f); setCoverPlanId(""); }}
                    >
                      Insure
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`delete-facility-${f.id}`}
                      onClick={() => act(() => api.delete(`/insurance/facilities/${f.id}`), "Facility removed")}
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- Facility dialog ---------------- */}
      <Dialog open={dialog === "facility"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="facility-dialog">
          <DialogHeader>
            <DialogTitle>Add a facility</DialogTitle>
            <DialogDescription>Warehouses, depots, yards and workshops you want covered.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveFacility} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="facility-name-input" required value={fForm.name} onChange={(e) => setFForm({ ...fForm, name: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Type</Label>
                <Select value={fForm.facility_type} onValueChange={(v) => setFForm({ ...fForm, facility_type: v })}>
                  <SelectTrigger data-testid="facility-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["warehouse", "depot", "workshop", "yard", "office", "plant"].map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Select value={fForm.country} onValueChange={(v) => setFForm({ ...fForm, country: v })}>
                  <SelectTrigger data-testid="facility-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>City</Label>
                <Input data-testid="facility-city-input" value={fForm.city} onChange={(e) => setFForm({ ...fForm, city: e.target.value })} />
              </div>
              <div>
                <Label>Value</Label>
                <Input data-testid="facility-value-input" type="number" min="0" step="0.01" value={fForm.value} onChange={(e) => setFForm({ ...fForm, value: e.target.value })} />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={fForm.currency} onValueChange={(v) => setFForm({ ...fForm, currency: v })}>
                  <SelectTrigger data-testid="facility-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Address</Label>
                <Input data-testid="facility-address-input" value={fForm.address} onChange={(e) => setFForm({ ...fForm, address: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-facility-button" className="w-full sm:w-auto">Add facility</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Insure facility dialog ---------------- */}
      <Dialog open={!!coverFacility} onOpenChange={(v) => !v && setCoverFacility(null)}>
        <DialogContent className="sm:max-w-md" data-testid="insure-facility-dialog">
          <DialogHeader>
            <DialogTitle>Insure {coverFacility?.name}</DialogTitle>
            <DialogDescription>
              {coverFacility?.value
                ? `Sum insured ${money(coverFacility.value, coverFacility.currency)} — pick a facility plan.`
                : "Give the facility a value first so the premium can be priced."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={insureFacility} className="space-y-3">
            <div>
              <Label>Plan</Label>
              <Select value={coverPlanId} onValueChange={setCoverPlanId}>
                <SelectTrigger data-testid="facility-plan-select"><SelectValue placeholder="Pick a plan" /></SelectTrigger>
                <SelectContent>
                  {plans.filter((p) => ["business", "liability"].includes(p.cover_type)).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.provider_name} — {p.name} ({p.rate_pct}%)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-facility-cover-button" disabled={!coverPlanId} className="w-full sm:w-auto">
                Take cover
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Provider dialog ---------------- */}
      <Dialog open={dialog === "provider"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="provider-dialog">
          <DialogHeader>
            <DialogTitle>{isAdmin ? "Add an insurer" : "Register as an insurer"}</DialogTitle>
            <DialogDescription>
              Say which cover you write, which countries you serve and whether you take international risks.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveProvider} className="space-y-3">
            <div>
              <Label>Insurer name</Label>
              <Input data-testid="provider-name-input" required value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} />
            </div>
            <div>
              <Label>Cover written</Label>
              <div className="mt-1 flex flex-wrap gap-2" data-testid="provider-cover-types">
                {COVERS.map(([k, label]) => (
                  <button
                    type="button"
                    key={k}
                    data-testid={`provider-cover-${k}`}
                    onClick={() => toggleCover(k)}
                    className={`border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                      pForm.cover_types.includes(k)
                        ? "border-[#002FA7] bg-[#002FA7] text-white"
                        : "border-border bg-white text-slate-600 hover:border-slate-400"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Countries served</Label>
                <Select
                  value={pForm.countries[0] || "ALL"}
                  onValueChange={(v) => setPForm({ ...pForm, countries: v === "ALL" ? [] : [v] })}
                >
                  <SelectTrigger data-testid="provider-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="ALL">Every country</SelectItem>
                    {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Base currency</Label>
                <Select value={pForm.currency} onValueChange={(v) => setPForm({ ...pForm, currency: v })}>
                  <SelectTrigger data-testid="provider-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                data-testid="provider-international-checkbox"
                checked={pForm.international}
                onChange={(e) => setPForm({ ...pForm, international: e.target.checked })}
                className="h-4 w-4 accent-[#002FA7]"
              />
              Writes international / cross-border risks
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Contact person</Label>
                <Input data-testid="provider-contact-input" value={pForm.contact_person} onChange={(e) => setPForm({ ...pForm, contact_person: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="provider-email-input" type="email" value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid="provider-phone-input" value={pForm.phone} onChange={(e) => setPForm({ ...pForm, phone: e.target.value })} />
              </div>
              <div>
                <Label>Regulator / authority</Label>
                <Input data-testid="provider-regulator-input" value={pForm.regulator} onChange={(e) => setPForm({ ...pForm, regulator: e.target.value })} />
              </div>
              <div>
                <Label>Licence no.</Label>
                <Input data-testid="provider-licence-input" value={pForm.licence_no} onChange={(e) => setPForm({ ...pForm, licence_no: e.target.value })} />
              </div>
              <div>
                <Label>Rating (0-5)</Label>
                <Input data-testid="provider-rating-input" type="number" step="0.1" min="0" max="5" value={pForm.rating} onChange={(e) => setPForm({ ...pForm, rating: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>What you cover</Label>
              <Textarea data-testid="provider-description-input" rows={2} value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-provider-button" className="w-full sm:w-auto">Save insurer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Plan dialog ---------------- */}
      <Dialog open={dialog === "plan"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="plan-dialog">
          <DialogHeader>
            <DialogTitle>Publish a cover plan</DialogTitle>
            <DialogDescription>
              Premiums are priced as a % of the sum insured with a minimum, so buyers get an instant price.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={savePlan} className="space-y-3">
            <div>
              <Label>Plan name</Label>
              <Input data-testid="plan-name-input" required value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Cover type</Label>
                <Select value={planForm.cover_type} onValueChange={(v) => setPlanForm({ ...planForm, cover_type: v })}>
                  <SelectTrigger data-testid="plan-cover-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COVERS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Charged</Label>
                <Select value={planForm.basis} onValueChange={(v) => setPlanForm({ ...planForm, basis: v })}>
                  <SelectTrigger data-testid="plan-basis-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BASES.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scope</Label>
                <Select value={planForm.scope} onValueChange={(v) => setPlanForm({ ...planForm, scope: v })}>
                  <SelectTrigger data-testid="plan-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Local (same country)</SelectItem>
                    <SelectItem value="international">International</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={planForm.currency} onValueChange={(v) => setPlanForm({ ...planForm, currency: v })}>
                  <SelectTrigger data-testid="plan-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rate % of sum insured</Label>
                <Input data-testid="plan-rate-input" type="number" step="0.01" min="0" value={planForm.rate_pct} onChange={(e) => setPlanForm({ ...planForm, rate_pct: e.target.value })} />
              </div>
              <div>
                <Label>Minimum premium</Label>
                <Input data-testid="plan-min-premium-input" type="number" step="0.01" min="0" value={planForm.min_premium} onChange={(e) => setPlanForm({ ...planForm, min_premium: e.target.value })} />
              </div>
              <div>
                <Label>Excess</Label>
                <Input data-testid="plan-excess-input" type="number" step="0.01" min="0" value={planForm.excess} onChange={(e) => setPlanForm({ ...planForm, excess: e.target.value })} />
              </div>
              <div>
                <Label>Max cover (blank = no cap)</Label>
                <Input data-testid="plan-max-cover-input" type="number" step="0.01" min="0" value={planForm.max_cover} onChange={(e) => setPlanForm({ ...planForm, max_cover: e.target.value })} />
              </div>
              <div>
                <Label>Term (days)</Label>
                <Input data-testid="plan-term-input" type="number" min="1" value={planForm.term_days} onChange={(e) => setPlanForm({ ...planForm, term_days: e.target.value })} />
              </div>
              <div>
                <Label>Country (blank = all)</Label>
                <Select
                  value={planForm.countries[0] || "ALL"}
                  onValueChange={(v) => setPlanForm({ ...planForm, countries: v === "ALL" ? [] : [v] })}
                >
                  <SelectTrigger data-testid="plan-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="ALL">Every country</SelectItem>
                    {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes / what is covered</Label>
              <Textarea data-testid="plan-notes-input" rows={2} value={planForm.notes} onChange={(e) => setPlanForm({ ...planForm, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-plan-button" className="w-full sm:w-auto">Publish plan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Enquiry dialog ---------------- */}
      <Dialog open={dialog === "enquiry"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="enquiry-dialog">
          <DialogHeader>
            <DialogTitle>Request a cover quote</DialogTitle>
            <DialogDescription>
              Every insurer writing that cover in your country is asked to quote. You choose which premium to accept.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendEnquiry} className="space-y-3">
            <div>
              <Label>Cover needed</Label>
              <Select value={eForm.cover_type} onValueChange={(v) => setEForm({ ...eForm, cover_type: v })}>
                <SelectTrigger data-testid="enquiry-cover-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COVERS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Sum insured</Label>
                <Input data-testid="enquiry-value-input" type="number" min="1" required value={eForm.insured_value} onChange={(e) => setEForm({ ...eForm, insured_value: e.target.value })} />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={eForm.currency} onValueChange={(v) => setEForm({ ...eForm, currency: v })}>
                  <SelectTrigger data-testid="enquiry-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Country</Label>
                <Select value={eForm.country} onValueChange={(v) => setEForm({ ...eForm, country: v })}>
                  <SelectTrigger data-testid="enquiry-country-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destination (optional)</Label>
                <Select value={eForm.dest_country || "NONE"} onValueChange={(v) => setEForm({ ...eForm, dest_country: v === "NONE" ? "" : v })}>
                  <SelectTrigger data-testid="enquiry-dest-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="NONE">Not a transit risk</SelectItem>
                    {COUNTRIES.map(([c, n]) => <SelectItem key={c} value={c}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>What needs covering</Label>
              <Textarea data-testid="enquiry-details-input" rows={3} value={eForm.details} onChange={(e) => setEForm({ ...eForm, details: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-enquiry-button" className="w-full sm:w-auto">Send enquiry</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Respond dialog ---------------- */}
      <Dialog open={!!respondTo} onOpenChange={(v) => !v && setRespondTo(null)}>
        <DialogContent className="sm:max-w-md" data-testid="respond-dialog">
          <DialogHeader>
            <DialogTitle>Quote this risk</DialogTitle>
            <DialogDescription>
              {respondTo ? `${coverLabel(respondTo.cover_type)} · ${money(respondTo.insured_value, respondTo.currency)} sum insured` : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendResponse} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Premium</Label>
                <Input data-testid="response-premium-input" type="number" step="0.01" min="0.01" required value={rForm.premium} onChange={(e) => setRForm({ ...rForm, premium: e.target.value })} />
              </div>
              <div>
                <Label>Currency</Label>
                <Select value={rForm.currency} onValueChange={(v) => setRForm({ ...rForm, currency: v })}>
                  <SelectTrigger data-testid="response-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Excess</Label>
                <Input data-testid="response-excess-input" type="number" step="0.01" min="0" value={rForm.excess} onChange={(e) => setRForm({ ...rForm, excess: e.target.value })} />
              </div>
              <div>
                <Label>Term (days)</Label>
                <Input data-testid="response-term-input" type="number" min="1" value={rForm.term_days} onChange={(e) => setRForm({ ...rForm, term_days: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Note / conditions</Label>
              <Textarea data-testid="response-note-input" rows={2} value={rForm.note} onChange={(e) => setRForm({ ...rForm, note: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-response-button" className="w-full sm:w-auto">Send quote</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---------------- Claim dialog ---------------- */}
      <Dialog open={dialog === "claim"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>File a claim</DialogTitle>
            <DialogDescription>The insurer is notified straight away and can move the claim through assessment.</DialogDescription>
          </DialogHeader>
          <form onSubmit={fileClaim} className="space-y-3">
            <div>
              <Label>Policy</Label>
              <Select value={cForm.policy_id} onValueChange={(v) => setCForm({ ...cForm, policy_id: v })}>
                <SelectTrigger data-testid="claim-policy-select"><SelectValue placeholder="Pick a policy" /></SelectTrigger>
                <SelectContent>
                  {policies.filter((p) => p.status === "active").map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.policy_number} — {coverLabel(p.cover_type)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Incident date</Label>
                <Input data-testid="claim-date-input" type="date" required value={cForm.incident_date} onChange={(e) => setCForm({ ...cForm, incident_date: e.target.value })} />
              </div>
              <div>
                <Label>Amount claimed</Label>
                <Input data-testid="claim-amount-input" type="number" step="0.01" min="0.01" required value={cForm.amount} onChange={(e) => setCForm({ ...cForm, amount: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>What happened</Label>
              <Textarea data-testid="claim-description-input" rows={3} required value={cForm.description} onChange={(e) => setCForm({ ...cForm, description: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-claim-button" className="w-full sm:w-auto">File claim</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
